import React from "react";
import { useNavigate, useParams } from "react-router-dom";
import PageContainer from "@/components/layouts/PageContainer";
import { Heading } from "@/components/heading";
import { Text } from "@/components/text";
import { Button } from "@/components/button";
import { Fieldset, Legend } from "@/components/fieldset";
import { Badge } from "@/components/badge";
import { ArrowLeftIcon, PrinterIcon } from "@heroicons/react/20/solid";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/table";
import {
  useQueryGetExamReport,
  useQueryGetExamReports,
} from "@/apis/exam_reports";
import type {
  ISectionDomain,
  ICategoryStat,
  IExamReport,
  ISubjectReport,
} from "@/apis/exam_papers/getExamReport";

const ExamReportDetailPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  if (!id) {
    navigate("/exam_results");
    return null;
  }

  // Query exam report data
  const { data: report, isLoading, error } = useQueryGetExamReport(id);

  // Query recent exam reports (only when current report is loaded)
  const { data: recentReportsData, isLoading: recentLoading } =
    useQueryGetExamReports(
      {
        user_id: report?.user_id || "",
        limit: 5,
        sort: "exam_started_at",
        sort_order: "desc",
      },
      !!report?.user_id // Only fetch when we have user_id
    );

  const recentReports = recentReportsData?.items || [];

  // Go back to list
  const handleBack = () => {
    navigate("/exam_results");
  };

  // Navigate to print report
  const handlePrintReport = () => {
    if (report?.exam_progress_id) {
      navigate(`/exam_progress/${report.exam_progress_id}/report`);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatScore = (score?: number | null) => {
    if (score === undefined || score === null) return "-";
    return score.toString();
  };

  const formatSubjectScore = (score?: number | null) => {
    if (score === undefined || score === null) return "0";
    return score.toString();
  };

  const getGradeDisplay = (grade?: number) => {
    if (!grade || grade === 0) return "-";
    return `Grade ${grade}`;
  };

  // Calculate accuracy percentage
  const formatAccuracy = (accuracy?: number) => {
    if (accuracy === undefined || accuracy === null) return "-";
    return `${(accuracy * 100).toFixed(1)}%`;
  };

  const getSubjectScore = (
    subjects: IExamReport["subjects"],
    subjectType: string
  ) => {
    const subject = subjects?.find((s) => s.subject === subjectType);
    return subject?.total_score;
  };

  const formatTotalScoreWithDetails = (result: IExamReport) => {
    const totalScore = formatScore(result.total_score);

    if (!result.subjects || result.subjects.length === 0) {
      return <div className="font-medium text-gray-900">{totalScore}</div>;
    }

    const rwScore = getSubjectScore(result.subjects, "reading_writing");
    const mathScore = getSubjectScore(result.subjects, "math");

    return (
      <div>
        <div className="font-medium text-gray-900">{totalScore}</div>
        <div className="text-xs text-gray-500 mt-1">
          R&W: {formatSubjectScore(rwScore)}, Math:{" "}
          {formatSubjectScore(mathScore)}
        </div>
      </div>
    );
  };

  // Render section analysis
  const renderSectionAnalysis = (
    section: ISubjectReport,
    sectionName: string
  ) => {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h4 className="text-sm font-medium text-gray-900">{sectionName}</h4>
          <div className="text-lg font-semibold text-gray-900">
            {formatScore(section.total_score)}
          </div>
        </div>

        {/* Domains Analysis */}
        {section.domains && section.domains.length > 0 && (
          <div className="space-y-2">
            <h5 className="text-xs font-medium text-gray-700 uppercase tracking-wider">
              Domain Analysis
            </h5>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {section.domains.map((domain: ISectionDomain) => (
                <div
                  key={domain.id}
                  className="p-3 border border-gray-200 rounded-md bg-gray-50"
                >
                  <div className="text-sm font-medium text-gray-900">
                    {domain.name}
                  </div>
                  <div className="text-xs text-gray-500 mt-1">
                    {domain.correct_answers || 0} /{" "}
                    {domain.total_questions || 0} correct
                    {domain.accuracy !== undefined && (
                      <span className="ml-2">
                        ({formatAccuracy(domain.accuracy)})
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  // Detail item component for consistent styling
  const DetailItem = ({
    label,
    value,
  }: {
    label: string;
    value: React.ReactNode;
  }) => (
    <div className="py-3 sm:grid sm:grid-cols-3 sm:gap-4">
      <div className="text-sm font-medium text-gray-500">{label}</div>
      <div className="mt-1 text-sm text-gray-900 sm:col-span-2 sm:mt-0">
        {value || "-"}
      </div>
    </div>
  );

  // Render recent performance table
  const renderRecentPerformanceTable = () => {
    if (recentLoading) {
      return (
        <div className="text-center py-8">
          <Text>Loading recent performance...</Text>
        </div>
      );
    }

    if (!recentReports || recentReports.length === 0) {
      return (
        <div className="text-center py-8 text-zinc-500">
          <Text>No recent exam results found.</Text>
        </div>
      );
    }

    return (
      <Table className="mt-4">
        <TableHead>
          <TableRow>
            <TableHeader>Exam Set</TableHeader>
            <TableHeader>Total Score</TableHeader>
            <TableHeader>Completion Date</TableHeader>
          </TableRow>
        </TableHead>
        <TableBody>
          {recentReports.map((report) => (
            <TableRow
              key={report.id}
              className="cursor-pointer hover:bg-gray-50"
              onClick={() => navigate(`/exam_results/${report.id}`)}
            >
              <TableCell>
                <div className="font-medium">
                  {report.exam_set?.title || "-"}
                </div>
                <div className="text-xs text-zinc-500">{report.exam_type}</div>
              </TableCell>
              <TableCell>{formatTotalScoreWithDetails(report)}</TableCell>
              <TableCell className="text-zinc-500">
                {formatDate(report.created_at)}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    );
  };

  // Loading state
  if (isLoading) {
    return (
      <PageContainer>
        <div className="p-8 text-center">
          <Text>Loading exam report...</Text>
        </div>
      </PageContainer>
    );
  }

  // Error state
  if (error || !report) {
    return (
      <PageContainer>
        <div className="p-8 text-center text-red-600">
          <Text>Failed to load exam report. Please try again.</Text>
          <Button onClick={handleBack} className="mt-4">
            Back to List
          </Button>
        </div>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button plain onClick={handleBack}>
              <ArrowLeftIcon className="h-5 w-5" />
              Back
            </Button>

            <div>
              <Heading>Exam Report Details</Heading>
              <Text className="text-zinc-500">
                Detailed analysis of {report.user.name}'s exam performance
              </Text>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Button onClick={handlePrintReport}>
              <PrinterIcon className="h-4 w-4" />
              Print Report
            </Button>
          </div>
        </div>

        {/* Student & Exam Information */}
        <div className="p-8 border border-gray-200 rounded-md bg-white">
          <Fieldset>
            <Legend>Exam Information</Legend>
            <div className="mt-4 border-t border-b border-gray-100">
              <dl className="divide-y divide-gray-100">
                <DetailItem label="Student Name" value={report.user.name} />
                <DetailItem label="Email" value={report.user.email} />
                <DetailItem
                  label="Grade"
                  value={getGradeDisplay(report.user.grade)}
                />
                <DetailItem label="School" value={report.user.school || "-"} />
                <DetailItem
                  label="Exam Set"
                  value={report.exam_set?.title || "-"}
                />
                <DetailItem label="Exam Type" value={report.exam_type} />
                <DetailItem
                  label="Total Score"
                  value={
                    <div className="text-lg font-semibold text-blue-600">
                      {formatScore(report.total_score)}
                    </div>
                  }
                />
                <DetailItem
                  label="Completed At"
                  value={formatDate(report.created_at)}
                />
              </dl>
            </div>
          </Fieldset>
        </div>

        {/* Score Breakdown - Only show for SAT exams */}
        {report.exam_type === "SAT" && report.subjects && report.subjects.length > 0 && (
          <div className="p-8 border border-gray-200 rounded-md bg-white">
            <Fieldset>
              <Legend>Score Analysis</Legend>
              <div className="mt-4 space-y-6">
                {report.subjects.map((subject) => (
                  <div
                    key={subject.subject}
                    className="border border-gray-200 rounded-md p-4"
                  >
                    {renderSectionAnalysis(
                      subject,
                      subject.subject === "reading_writing"
                        ? "Reading & Writing"
                        : "Math"
                    )}
                  </div>
                ))}
              </div>
            </Fieldset>
          </div>
        )}

        {/* Recent Performance */}
        <div className="p-8 border border-gray-200 rounded-md bg-white">
          <Fieldset>
            <Legend>Recent Performance</Legend>
            <div className="mt-4">
              <Text className="text-zinc-500 mb-4">
                Recent exam results from this student (last 5 attempts before
                this exam)
              </Text>
              {renderRecentPerformanceTable()}
            </div>
          </Fieldset>
        </div>
      </div>
    </PageContainer>
  );
};

export default ExamReportDetailPage;
